import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, User, Hash, Server, MessageSquare, Search } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useServerStore } from '../../store/serverStore';
import { useMessageStore } from '../../store/messageStore';
import { useFriendStore } from '../../store/friendStore';
import ProfileView from '../profile/ProfileView';

interface SearchModalProps {
  query: string;
  onClose: () => void;
}

// Mock search results
const mockUsers = [
  { id: 'user1', username: 'JaneDoe', avatar_url: null },
  { id: 'user2', username: 'JohnSmith', avatar_url: null },
  { id: 'user3', username: 'AlexWilson', avatar_url: null }
];

const mockServers = [
  { id: 'server1', name: 'Gaming Hub', icon_url: null },
  { id: 'server2', name: 'Project Alpha', icon_url: null }
];

const mockChannels = [
  { id: 'channel1', name: 'general', server_id: 'server1', server_name: 'Gaming Hub' },
  { id: 'channel2', name: 'announcements', server_id: 'server1', server_name: 'Gaming Hub' },
  { id: 'channel3', name: 'development', server_id: 'server2', server_name: 'Project Alpha' }
];

const mockMessages = [
  { 
    id: 'msg1', 
    content: 'Has anyone tried the new game?', 
    user_id: 'user1', 
    username: 'JaneDoe',
    channel_id: 'channel1',
    channel_name: 'general',
    server_id: 'server1',
    server_name: 'Gaming Hub',
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 2) // 2 hours ago
  },
  { 
    id: 'msg2', 
    content: 'We need to discuss the project timeline', 
    user_id: 'user2', 
    username: 'JohnSmith',
    channel_id: 'channel3',
    channel_name: 'development',
    server_id: 'server2',
    server_name: 'Project Alpha',
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 24) // 1 day ago
  }
];

const SearchModal: React.FC<SearchModalProps> = ({ query, onClose }) => {
  const { profile } = useAuthStore();
  const { setCurrentServer, setCurrentChannel } = useServerStore();
  const { setCurrentDmUser } = useMessageStore();
  const [activeTab, setActiveTab] = useState<'all' | 'users' | 'servers' | 'channels' | 'messages'>('all');
  const [searchResults, setSearchResults] = useState({
    users: [] as typeof mockUsers,
    servers: [] as typeof mockServers,
    channels: [] as typeof mockChannels,
    messages: [] as typeof mockMessages
  });
  const [showUserProfile, setShowUserProfile] = useState<string | null>(null);
  
  useEffect(() => {
    // In a real app, this would call an API to search
    const searchData = () => {
      const filteredUsers = mockUsers.filter(user => 
        user.username.toLowerCase().includes(query.toLowerCase())
      );
      
      const filteredServers = mockServers.filter(server => 
        server.name.toLowerCase().includes(query.toLowerCase())
      );
      
      const filteredChannels = mockChannels.filter(channel => 
        channel.name.toLowerCase().includes(query.toLowerCase())
      );
      
      const filteredMessages = mockMessages.filter(message => 
        message.content.toLowerCase().includes(query.toLowerCase())
      );
      
      setSearchResults({
        users: filteredUsers,
        servers: filteredServers,
        channels: filteredChannels,
        messages: filteredMessages
      });
    };
    
    searchData();
  }, [query]);
  
  const handleUserClick = (userId: string) => {
    setShowUserProfile(userId);
  };
  
  const handleServerClick = (serverId: string) => {
    const server = mockServers.find(s => s.id === serverId);
    if (server) {
      setCurrentDmUser(null);
      setCurrentServer(server);
      onClose();
    }
  };
  
  const handleChannelClick = (channelId: string, serverId: string) => {
    const server = mockServers.find(s => s.id === serverId);
    const channel = mockChannels.find(c => c.id === channelId);
    if (server && channel) {
      setCurrentDmUser(null);
      setCurrentServer(server);
      setCurrentChannel(channel);
      onClose();
    }
  };
  
  const handleMessageClick = (messageId: string, channelId: string, serverId: string) => {
    const server = mockServers.find(s => s.id === serverId);
    const channel = mockChannels.find(c => c.id === channelId);
    if (server && channel) {
      setCurrentDmUser(null);
      setCurrentServer(server);
      setCurrentChannel(channel);
      // In a real app, we would also scroll to the message
      onClose();
    }
  };
  
  const getFilteredResults = () => {
    if (activeTab === 'all') {
      return searchResults;
    }
    
    return {
      users: activeTab === 'users' ? searchResults.users : [],
      servers: activeTab === 'servers' ? searchResults.servers : [],
      channels: activeTab === 'channels' ? searchResults.channels : [],
      messages: activeTab === 'messages' ? searchResults.messages : []
    };
  };
  
  const filteredResults = getFilteredResults();
  const hasResults = 
    filteredResults.users.length > 0 || 
    filteredResults.servers.length > 0 || 
    filteredResults.channels.length > 0 || 
    filteredResults.messages.length > 0;
  
  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[200]">
      <div className="bg-bg-secondary rounded-lg w-full max-w-2xl h-[80vh] flex flex-col animate-fade-in">
        <div className="p-4 border-b border-border-color flex justify-between items-center">
          <div className="flex items-center">
            <Search className="h-5 w-5 text-accent-color mr-2" />
            <h2 className="text-lg font-semibold text-text-primary">
              Search Results for "{query}"
            </h2>
          </div>
          <button 
            className="text-text-secondary hover:text-text-primary transition-colors"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="border-b border-border-color">
          <div className="flex">
            <button
              className={`flex-1 py-2 text-center text-sm transition-colors ${
                activeTab === 'all' 
                  ? 'text-text-primary border-b-2 border-accent-color' 
                  : 'text-text-secondary hover:text-text-primary'
              }`}
              onClick={() => setActiveTab('all')}
            >
              All
            </button>
            <button
              className={`flex-1 py-2 text-center text-sm transition-colors ${
                activeTab === 'users' 
                  ? 'text-text-primary border-b-2 border-accent-color' 
                  : 'text-text-secondary hover:text-text-primary'
              }`}
              onClick={() => setActiveTab('users')}
            >
              Users
            </button>
            <button
              className={`flex-1 py-2 text-center text-sm transition-colors ${
                activeTab === 'servers' 
                  ? 'text-text-primary border-b-2 border-accent-color' 
                  : 'text-text-secondary hover:text-text-primary'
              }`}
              onClick={() => setActiveTab('servers')}
            >
              Servers
            </button>
            <button
              className={`flex-1 py-2 text-center text-sm transition-colors ${
                activeTab === 'channels' 
                  ? 'text-text-primary border-b-2 border-accent-color' 
                  : 'text-text-secondary hover:text-text-primary'
              }`}
              onClick={() => setActiveTab('channels')}
            >
              Channels
            </button>
            <button
              className={`flex-1 py-2 text-center text-sm transition-colors ${
                activeTab === 'messages' 
                  ? 'text-text-primary border-b-2 border-accent-color' 
                  : 'text-text-secondary hover:text-text-primary'
              }`}
              onClick={() => setActiveTab('messages')}
            >
              Messages
            </button>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          {hasResults ? (
            <div className="space-y-6">
              {(activeTab === 'all' || activeTab === 'users') && filteredResults.users.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-text-secondary uppercase mb-2 flex items-center">
                    <User className="h-4 w-4 mr-1" />
                    Users
                  </h3>
                  <div className="space-y-2">
                    {filteredResults.users.map(user => (
                      <div 
                        key={user.id}
                        className="p-2 rounded-md hover:bg-highlight/50 transition-colors cursor-pointer flex items-center"
                        onClick={() => handleUserClick(user.id)}
                      >
                        <div className="avatar w-8 h-8 mr-3 flex-shrink-0">
                          {user.avatar_url ? (
                            <img 
                              src={user.avatar_url} 
                              alt={user.username} 
                              className="w-8 h-8 object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 bg-accent-color text-white flex items-center justify-center rounded-full">
                              {user.username.charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <span className="text-text-primary">{user.username}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {(activeTab === 'all' || activeTab === 'servers') && filteredResults.servers.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-text-secondary uppercase mb-2 flex items-center">
                    <Server className="h-4 w-4 mr-1" />
                    Servers
                  </h3>
                  <div className="space-y-2">
                    {filteredResults.servers.map(server => (
                      <div 
                        key={server.id}
                        className="p-2 rounded-md hover:bg-highlight/50 transition-colors cursor-pointer flex items-center"
                        onClick={() => handleServerClick(server.id)}
                      >
                        <div className="avatar w-8 h-8 mr-3 flex-shrink-0">
                          {server.icon_url ? (
                            <img 
                              src={server.icon_url} 
                              alt={server.name} 
                              className="w-8 h-8 object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 bg-accent-color text-white flex items-center justify-center rounded-full">
                              {server.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <span className="text-text-primary">{server.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {(activeTab === 'all' || activeTab === 'channels') && filteredResults.channels.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-text-secondary uppercase mb-2 flex items-center">
                    <Hash className="h-4 w-4 mr-1" />
                    Channels
                  </h3>
                  <div className="space-y-2">
                    {filteredResults.channels.map(channel => (
                      <div 
                        key={channel.id}
                        className="p-2 rounded-md hover:bg-highlight/50 transition-colors cursor-pointer"
                        onClick={() => handleChannelClick(channel.id, channel.server_id)}
                      >
                        <div className="flex items-center">
                          <Hash className="h-4 w-4 text-accent-color mr-2" />
                          <span className="text-text-primary">{channel.name}</span>
                        </div>
                        <div className="text-text-muted text-xs mt-1 ml-6">
                          in {channel.server_name}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {(activeTab === 'all' || activeTab === 'messages') && filteredResults.messages.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-text-secondary uppercase mb-2 flex items-center">
                    <MessageSquare className="h-4 w-4 mr-1" />
                    Messages
                  </h3>
                  <div className="space-y-2">
                    {filteredResults.messages.map(message => (
                      <div 
                        key={message.id}
                        className="p-2 rounded-md hover:bg-highlight/50 transition-colors cursor-pointer"
                        onClick={() => handleMessageClick(message.id, message.channel_id, message.server_id)}
                      >
                        <div className="flex items-center">
                          <span className="text-accent-color font-medium">{message.username}:</span>
                          <span className="text-text-primary ml-2 truncate">{message.content}</span>
                        </div>
                        <div className="text-text-muted text-xs mt-1">
                          in #{message.channel_name} ({message.server_name})
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-text-secondary">
              <Search className="h-12 w-12 mb-2 text-text-muted" />
              <p>No results found for "{ query}"</p>
              <p className="text-sm mt-2">Try a different search term or category</p>
            </div>
          )}
        </div>
      </div>
      
      {showUserProfile && (
        <div className="absolute inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-bg-secondary rounded-lg w-full max-w-md animate-fade-in">
            <ProfileView 
              userId={showUserProfile} 
              isCurrentUser={showUserProfile === profile?.id}
              onClose={() => setShowUserProfile(null)} 
            />
          </div>
        </div>
      )}
    </div>
  , document.body);
};

export default SearchModal;